import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function DogFriendlyDiningInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "dog-friendly-places-to-eat-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Top Dog-Friendly Spots (1-5)",
    "section-2": "Top Dog-Friendly Spots (6-10)",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver",
    },
    {
      url: "https://gov.bc.ca",
      label: "BC Provincial Health Policy Document",
    },
    {
      url: "https://www.bringfido.com/restaurant/city/vancouver_bc_ca",
      label: "BringFido – Dog Friendly Restaurants in Vancouver",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location corresponds to the places mentioned in the article. 
  // We'll list 10. The id matches a unique text anchor for scrolling.
  // lat and lng must match text exactly.
  const locations = [
    {
      id: "score-on-davie",
      lat: 49.28181,
      lng: -123.13511,
      popupText: "Score on Davie",
    },
    {
      id: "tap-and-barrel",
      lat: 49.27052,
      lng: -123.10476,
      popupText: "Tap & Barrel Olympic Village",
    },
    {
      id: "provence-marinaside",
      lat: 49.27355,
      lng: -123.12351,
      popupText: "Provence Marinaside",
    },
    {
      id: "local-kitsilano",
      lat: 49.27300,
      lng: -123.15430,
      popupText: "LOCAL Public Eatery (Kits Beach)",
    },
    {
      id: "red-truck-beer",
      lat: 49.26880,
      lng: -123.09830,
      popupText: "Red Truck Beer Company",
    },
    {
      id: "six-acres",
      lat: 49.28349,
      lng: -123.10434,
      popupText: "Six Acres Gastown",
    },
    {
      id: "okini-restaurant-bar",
      lat: 49.21905,
      lng: -123.13650,
      popupText: "Okini Restaurant & Bar",
    },
    {
      id: "granville-island-public-market",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island Public Market & Courtyard",
    },
    {
      id: "chaise-lounge-main",
      lat: 49.24800,
      lng: -123.10110,
      popupText: "Chaise Lounge on Main",
    },
    {
      id: "cider-house-kits",
      lat: 49.27340,
      lng: -123.15290,
      popupText: "The Cider House Kitsilano",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Dog-Friendly Dining in Vancouver: Top Spots and Tips</title>
        <meta
          name="description"
          content="A detailed guide on the best dog-friendly places to eat and drink in Vancouver, including tips, local regulations, FAQs, and more."
        />
        <meta
          name="keywords"
          content="dog-friendly, Vancouver, restaurants, patios, cafes, breweries"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/dog-friendly-places-to-eat-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Dog-Friendly Dining in Vancouver: Top Spots and Tips"
        />
        <meta
          property="og:description"
          content="A detailed guide on the best dog-friendly places to eat and drink in Vancouver, including tips, local regulations, FAQs, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/dog-friendly-places-to-eat-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/dog-friendly-places-to-eat-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Dog-Friendly Dining in Vancouver: Top Spots and Tips"
        />
        <meta
          name="twitter:description"
          content="A detailed guide on dog-friendly eateries in Vancouver, complete with local rules and FAQs."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/dog-friendly-places-to-eat-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="People dining with a dog in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Dog-friendly dining is more than just a passing trend – it’s
              become an essential part of Vancouver’s vibrant food culture.
              With numerous patios welcoming our four-legged companions,
              it’s easier than ever to explore the city’s culinary scene
              without leaving your pup at home. Whether you crave a brunch
              by the beach, a craft brew in a retro taproom, or an upscale
              French meal along the waterfront, Vancouver has an option for
              you and your dog. In this guide, we’ll explore ten of the best
              dog-friendly eateries across the city, outline local
              regulations and best practices, answer your FAQs, and provide
              valuable resources.
            </p>

            {/* SECTION: Section 1 (Spots 1-5) */}
            <h2 id="section-1">Top Dog-Friendly Spots (1–5)</h2>
            <ol>
              <li>
                <p
                  ref={(el) => setSpotRef("score-on-davie", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Score on Davie</strong>{" "}
                  <Marker
                    coordY={49.28181}
                    coordX={-123.13511}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 1262 Davie St, Vancouver, BC V6E 1N3 <br />
                  Hours: Mon–Fri 11am–1am; Sat–Sun 10am–1am <br />
                  Cost: $$ (cocktails $10+, mains $15–$25) <br />
                  Highlights: Over-the-top Caesar cocktails, lively patio.
                  Dogs must remain just outside the patio fence, but staff
                  happily provides water bowls. Located in the West End, near
                  Davie Village and the Beach Avenue waterfront.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("tap-and-barrel", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Tap & Barrel (Olympic Village)</strong>{" "}
                  <Marker
                    coordY={49.27052}
                    coordX={-123.10476}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 75 Athletes Way, Vancouver, BC V5Y 0B1 <br />
                  Hours: Mon–Thu 11am–12am; Fri 11am–1am; Sat 10am–1am; Sun
                  10am–12am <br />
                  Cost: $$ (pub fare $15–$30) <br />
                  Highlights: Huge waterfront patio, dog-friendly designated
                  areas, local craft beer on tap. Perfect for a post-meal
                  stroll on the seawall with your pup.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("provence-marinaside", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Provence Marinaside</strong>{" "}
                  <Marker
                    coordY={49.27355}
                    coordX={-123.12351}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 1177 Marinaside Crescent, Vancouver, BC V6Z 2Y3 <br />
                  Hours: Mon–Fri 8am–10pm; Sat–Sun 9am–10pm <br />
                  Cost: $$$ (upscale French dining) <br />
                  Highlights: French-inspired cuisine on Yaletown’s scenic
                  seawall. Dogs can sit just outside the patio fence, with
                  water bowls readily available. A classy option for those
                  who want a more refined dog-friendly experience.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("local-kitsilano", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>LOCAL Public Eatery (Kitsilano)</strong>{" "}
                  <Marker
                    coordY={49.27300}
                    coordX={-123.15430}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 2210 Cornwall Ave, Vancouver, BC V6K 1B5 <br />
                  Hours: Vary by day; generally 10am–12am or later <br />
                  Cost: $$ (pub-style meals, $14–$20) <br />
                  Highlights: Across from Kits Beach, ocean views, bustling
                  patio. Dogs stay on the outer edge of the patio fence,
                  staff offers water upon request. Great for a beach day
                  followed by burgers and beers.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("red-truck-beer", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Red Truck Beer Company</strong>{" "}
                  <Marker
                    coordY={49.26880}
                    coordX={-123.09830}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 295 E 1st Ave, Vancouver, BC V5T 1A7 <br />
                  Hours: Daily 11am–10pm (approx.) <br />
                  Cost: $–$$ (beer flights $8–$12, food $12–$18) <br />
                  Highlights: Spacious sun-drenched patio, retro truck-stop
                  theme, friendly staff who often bring water bowls for pups.
                  Occasional “Barks and Brews” events and live music.
                </p>
              </li>
            </ol>

            {/* SECTION: Section 2 (Spots 6-10) */}
            <h2 id="section-2">Top Dog-Friendly Spots (6–10)</h2>
            <ol start="6">
              <li>
                <p
                  ref={(el) => setSpotRef("six-acres", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Six Acres (Gastown)</strong>{" "}
                  <Marker
                    coordY={49.28349}
                    coordX={-123.10434}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 203 Carrall St, Vancouver, BC V6B 2J2 <br />
                  Hours: Daily 11:30am–1am (weekend brunch from 10am) <br />
                  Cost: $$ (share plates, craft beer) <br />
                  Highlights: Small curbside patio in historic Gastown,
                  perfect for people-watching. Dogs stay leashed by your
                  table; staff provides water. Cozy, heritage vibe.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("okini-restaurant-bar", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Okini Restaurant & Bar (Kerrisdale)</strong>{" "}
                  <Marker
                    coordY={49.21905}
                    coordX={-123.13650}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 1864 W 57th Ave, Vancouver, BC V6P 1T7 <br />
                  Hours: Dinner Tue–Sun from 3:30pm <br />
                  Cost: $$$ (fine-dining; tasting menus) <br />
                  Highlights: Heated garden atrium, a special dog menu,
                  “pawgarita” mocktails, and gourmet dog-friendly dishes.
                  Upscale ambiance; ideal for a fancy date with your pup.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("granville-island-public-market", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Granville Island Public Market & Courtyard</strong>{" "}
                  <Marker
                    coordY={49.27262}
                    coordX={-123.13524}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 1689 Johnston St, Vancouver, BC V6H 3R9 <br />
                  Hours: Public Market 9am–6pm, outdoor areas 24/7 <br />
                  Cost: $–$$ (varied street food and vendor stalls) <br />
                  Highlights: Dogs not allowed inside the indoor market area,
                  but plenty of outdoor spots to dine with your pup (covered
                  courtyards, picnic tables). Some on-site restaurants have
                  patios that allow dogs; a fantastic hub of local food and
                  artisan shops.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("chaise-lounge-main", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>Chaise Lounge on Main</strong>{" "}
                  <Marker
                    coordY={49.24800}
                    coordX={-123.10110}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 4444 Main St, Vancouver, BC V5V 3R2 <br />
                  Hours: Mon–Thu 4pm–12am; Fri–Sat until 1am; weekend brunch
                  from 10am <br />
                  Cost: $$ (casual comfort food, $12–$20) <br />
                  Highlights: Neighborhood pub feel, cozy patio on Main St,
                  dog bowls on request. Tasty nachos, rotating taps, and a
                  less-crowded vibe than downtown spots.
                </p>
              </li>
              <li>
                <p
                  ref={(el) => setSpotRef("cider-house-kits", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <strong>The Cider House (Kitsilano)</strong>{" "}
                  <Marker
                    coordY={49.27340}
                    coordX={-123.15290}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                </p>
                <p>
                  Address: 1602 Yew St, Vancouver, BC V6K 3E7 <br />
                  Hours: Daily 12pm–11pm <br />
                  Cost: $$ (cider flights ~$15, apps ~$8–$14) <br />
                  Highlights: Cozy patio near Kits Beach, rotating BC ciders,
                  water bowls for furry visitors. Great place to unwind after
                  a beach walk with your dog.
                </p>
              </li>
            </ol>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              British Columbia’s health regulations prohibit dogs from
              entering indoor dining areas (except service animals). Many
              patios in Vancouver now welcome dogs outdoors, but each
              establishment’s approach may vary—some require dogs to stay
              outside the railing, while others designate specific tables for
              dog owners. Always call ahead to confirm the latest policy.
            </p>
            <ul>
              <li>
                Keep dogs leashed and under control. Retractable leashes can
                be a hazard in crowded patios.
              </li>
              <li>
                Most Vancouver spots ask that you don’t feed dogs from your
                plate; check if they have a dedicated dog menu or treats.
              </li>
              <li>
                If your dog is anxious or disruptive, be prepared to step
                away or take food to-go.
              </li>
              <li>
                Clean up any messes immediately and be respectful of other
                diners—some might fear or be allergic to dogs.
              </li>
            </ul>
            <p>
              For further details, consult the BC Provincial Health Policy
              on pet dogs in outdoor dining areas, and remember to always
              comply with each venue’s guidelines.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are dogs allowed inside restaurants in Vancouver?</h3>
              <p>
                No, dogs generally aren’t permitted indoors due to health
                regulations (except certified service animals). Dog-friendly
                dining is almost always restricted to patios or outdoor
                seating.
              </p>

              <h3>2. Do all patios in Vancouver allow dogs?</h3>
              <p>
                Not all patios are dog-friendly. It’s up to each business to
                apply for approval and set guidelines. Always call ahead or
                check social media to confirm if a patio welcomes dogs and
                how they accommodate them.
              </p>

              <h3>3. Can I share food from my plate with my dog?</h3>
              <p>
                It’s best not to—restaurant foods may contain ingredients
                harmful to dogs. Some places do offer dog-specific treats or
                menus. If in doubt, bring your own dog-friendly snacks.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s dog-friendly dining scene is thriving, offering
              countless patios, pubs, and restaurants that warmly welcome
              your furry companion. By respecting each venue’s rules and
              ensuring your dog is well-behaved, you’ll help maintain this
              inclusive culture. Grab a leash and get ready to explore the
              city’s diverse culinary landscape with your pup—there’s no
              better way to enjoy Vancouver’s outdoor charm.
            </p>
            <p>
              <strong>Disclaimer:</strong> While every effort is made to
              ensure accuracy, policies may change without notice. Always
              confirm with the venue before visiting, and prioritize your
              dog’s comfort and safety.
            </p>

            {/* BOTTOM COMPONENT: references, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}